import Icon from '@components/Icon';
import { Validation } from '@ef-core/validation';
import { concatStyles } from '@styles/domain';
import { FC, useMemo } from 'react';
import * as styles from './index.css';

interface Props extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'checked'
> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
  validate?: (value?: boolean) => Validation | undefined;
}

const Checkbox: FC<Props> = ({
  checked,
  onChange,
  children,
  disabled = false,
  className,
  validate,
  ...props
}) => {
  const validation = useMemo(() => {
    if (!validate) {
      return;
    }

    return validate(checked);
  }, [validate, checked]);

  const displayError = validation?.valid === false;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  return (
    <label className={[styles.wrapper, className].filter(Boolean).join(' ')}>
      <input
        {...props}
        type='checkbox'
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        className={styles.input}
      />
      <Icon
        name={checked ? 'check_box' : 'check_box_outline_blank'}
        fill={checked}
        size='md'
        color={displayError ? 'error' : checked ? 'primary' : 'inherit'}
        className={styles.checkboxIcon}
      />
      <span
        className={concatStyles([
          styles.labelText,
          displayError ? styles.labelError : '',
        ])}
      >
        {children}
      </span>
      {displayError && validation?.error && (
        <span className={styles.errorMessage} role='alert'>
          {validation.error}
        </span>
      )}
    </label>
  );
};

export default Checkbox;
